import {
  ExposureData,
  HedgeExposureData,
  HedgeNotionalData,
  NotionalData,
} from '@/models/FundLevel/Rebalancing/ExposureCalculation';
import { PropsedExposureTrades } from '@/models/FundLevel/Rebalancing/ExposureCalculation/ProposedTrades';

import { ActionType, getType } from 'typesafe-actions';

import * as exposureCalculationActions from './actions';
import { HedgeCloseoutTradeData } from './actions';

type ExposureCalculationActionType = ActionType<
  typeof exposureCalculationActions
>;

export type ExposureCalculationState = {
  exposures: {
    data: ExposureData[];
    loading: boolean;
  };
  hedges: {
    data: HedgeNotionalData;
    loading: boolean;
  };
  proposedTrades: {
    data: PropsedExposureTrades;
    loading: boolean;
  };
  sendProposedTradesLoading: boolean;
  hedgeCloseoutTrades: {
    data: HedgeCloseoutTradeData[];
    loading: boolean;
  };
  closeoutExposureLedgerIds: number[];
  closeoutHedgeIds: number[];
};
const initialState: ExposureCalculationState = {
  exposures: {
    data: [],
    loading: false,
  },
  hedges: {
    data: {
      hedge_data: [],
      maturity_dates: {},
    },
    loading: false,
  },

  proposedTrades: {
    data: {
      expo_data: [],
      maturity_dates: {},
      hedge_ratio_data: [],
      unmatched_hedge_ratio_data: [],
      report_data: [],
      log: {},
    },
    loading: false,
  },
  sendProposedTradesLoading: false,
  hedgeCloseoutTrades: {
    data: [],
    loading: false,
  },
  closeoutExposureLedgerIds: [],
  closeoutHedgeIds: [],
};

export const exposureCalculationReducer = (
  state: ExposureCalculationState = initialState,
  action: ExposureCalculationActionType,
): ExposureCalculationState => {
  switch (action.type) {
    case getType(exposureCalculationActions.getHedgeExposureData.request):
      return {
        ...state,
        exposures: {
          ...state.exposures,
          loading: true,
        },
      };
    case getType(exposureCalculationActions.getHedgeExposureData.success):
      return {
        ...state,
        exposures: {
          ...state.exposures,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(exposureCalculationActions.getHedgeExposureData.failure):
      return {
        ...state,
        exposures: {
          ...state.exposures,
          loading: false,
        },
      };

    //hedges
    case getType(exposureCalculationActions.getHedgeNotionalData.request):
      return {
        ...state,
        hedges: {
          ...state.hedges,
          loading: true,
        },
      };
    case getType(exposureCalculationActions.getHedgeNotionalData.success):
      return {
        ...state,
        hedges: {
          ...state.hedges,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(exposureCalculationActions.getHedgeNotionalData.failure):
      return {
        ...state,
        hedges: {
          ...state.hedges,
          loading: false,
        },
      };

    //proposed trades
    case getType(exposureCalculationActions.getProposedTrades.request):
      return {
        ...state,
        proposedTrades: {
          ...state.proposedTrades,
          loading: true,
        },
      };
    case getType(exposureCalculationActions.getProposedTrades.success):
      return {
        ...state,
        proposedTrades: {
          ...state.proposedTrades,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(exposureCalculationActions.getProposedTrades.failure):
      return {
        ...state,
        proposedTrades: {
          ...state.proposedTrades,
          loading: false,
        },
      };

    // add close-out trades to proposed trades
    case getType(exposureCalculationActions.addCloseOutTradesToProposed): {
      const newCloseoutExposureLedgerIds: number[] = [];
      const newCloseoutHedgeIds: number[] = [];

      action.payload.trades.forEach((trade) => {
        if (trade.expo_ledger_ids && trade.expo_ledger_ids.length > 0) {
          newCloseoutExposureLedgerIds.push(...trade.expo_ledger_ids);
        }
        if (trade.hedge_ids && trade.hedge_ids.length > 0) {
          newCloseoutHedgeIds.push(...trade.hedge_ids);
        }
      });

      return {
        ...state,
        proposedTrades: {
          ...state.proposedTrades,
          data: {
            ...state.proposedTrades.data,
            expo_data: [
              ...state.proposedTrades.data.expo_data,
              ...action.payload.trades,
            ],
          },
        },
        closeoutExposureLedgerIds: [
          ...state.closeoutExposureLedgerIds,
          ...newCloseoutExposureLedgerIds,
        ],
        closeoutHedgeIds: [...state.closeoutHedgeIds, ...newCloseoutHedgeIds],
      };
    }

    // send proposed trades loading
    case getType(exposureCalculationActions.sendTrades.request):
      return {
        ...state,
        sendProposedTradesLoading: true,
      };
    case getType(exposureCalculationActions.sendTrades.success):
      return {
        ...state,
        sendProposedTradesLoading: false,
      };
    case getType(exposureCalculationActions.sendTrades.failure):
      return {
        ...state,
        sendProposedTradesLoading: false,
      };

    // hedge closeout trades
    case getType(
      exposureCalculationActions.generateCloseoutTradesFromHedges.request,
    ):
      return {
        ...state,
        hedgeCloseoutTrades: {
          ...state.hedgeCloseoutTrades,
          loading: true,
        },
      };
    case getType(
      exposureCalculationActions.generateCloseoutTradesFromHedges.success,
    ):
      return {
        ...state,
        hedgeCloseoutTrades: {
          data: action.payload.closeout_trades,
          loading: false,
        },
      };
    case getType(
      exposureCalculationActions.generateCloseoutTradesFromHedges.failure,
    ):
      return {
        ...state,
        hedgeCloseoutTrades: {
          ...state.hedgeCloseoutTrades,
          loading: false,
        },
      };

    case getType(exposureCalculationActions.clearHedgeCloseoutTrades):
      return {
        ...state,
        hedgeCloseoutTrades: {
          data: [],
          loading: false,
        },
      };

    case getType(exposureCalculationActions.clearCloseoutExposureLedgerIds):
      return {
        ...state,
        closeoutExposureLedgerIds: [],
      };

    case getType(exposureCalculationActions.clearCloseoutHedgeIds):
      return {
        ...state,
        closeoutHedgeIds: [],
      };

    //reset
    case getType(exposureCalculationActions.resetExposureAndHedges):
      return {
        ...initialState,
      };

    default: {
      return state;
    }
  }
};

export default exposureCalculationReducer;
